<template>
  <div id="dashboard-distribution">
    <WizardAlert section="dashboardDistribution" alertClass="my-4">
      <template v-slot:text>
        <p>
          {{ $t('views.dashboardDetail.distribution.wizard.a') }}
        </p>
        <p>
          {{ $t('views.dashboardDetail.distribution.wizard.b') }}
        </p>
        <p>
          {{ $t('views.dashboardDetail.distribution.wizard.c') }}
        </p>
      </template>
    </WizardAlert>

    <v-card
      class="px-5 py-2 mt-4 mb-0 elevation-0"
      :class="{ 'sticky-filters': user.preferences.stickyFilters }"
    >
      <PositionsFiltersAndViewMode
        ref="positionsFilters"
        type="position"
        :positionType.sync="positionType"
        showZeroPositions
        @filterPositions="filterPositions"
      />
    </v-card>

    <SomePositionIncomplete />

    <v-row>
      <v-col cols="12" md="12" lg="12" class="mt-4 py-0">
        <PositionsBySummary ref="positionsByTicker" bySummary="byTicker" />
      </v-col>
      <v-col cols="12" md="12" lg="12" class="mt-4 py-0">
        <PositionsBySummary ref="positionsByType" bySummary="byType" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          ['stock', 'fund', 'etf', 'plan', 'bond', 'putOption', 'callOption', 'right'].indexOf(
            positionType
          ) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsByCurrency" bySummary="byCurrency" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          ['stock', 'fund', 'etf', 'plan', 'bond', 'putOption', 'callOption', 'right'].indexOf(
            positionType
          ) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsBySector" bySummary="bySector" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          ['stock', 'etf', 'plan', 'bond', 'putOption', 'callOption', 'right'].indexOf(
            positionType
          ) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsByCountry" bySummary="byCountry" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          ['stock', 'etf', 'fund', 'plan', 'bond', 'putOption', 'callOption', 'right'].indexOf(
            positionType
          ) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsByExposureCountry" bySummary="byExposureCountry" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          ['stock', 'etf', 'plan', 'putOption', 'callOption', 'right'].indexOf(positionType) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsByExchange" bySummary="byExchange" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          [
            'stock',
            'fund',
            'etf',
            'bond',
            'plan',
            'putOption',
            'callOption',
            'right',
            'crypto',
            'commodity',
          ].indexOf(positionType) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsByTags" bySummary="byTags" />
      </v-col>
      <v-col
        v-show="
          ['all', 'allNeCash'].indexOf(positionType) > -1 ||
          ['fund', 'etf', 'plan'].indexOf(positionType) > -1
        "
        cols="12"
        md="12"
        lg="12"
        class="mt-4 py-0"
      >
        <PositionsBySummary ref="positionsByManager" bySummary="byManager" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { get } from 'lodash'
import { mapState } from 'vuex'
import PositionsFiltersAndViewMode from '@/views/components/common/positions/PositionsFiltersAndViewMode'
import WizardAlert from '@/views/components/common/WizardAlert'
import PositionsBySummary from '@/views/components/dashboardDetail/summary/PositionsBySummary'
import SomePositionIncomplete from '@/views/components/common/positions/SomePositionIncomplete.vue'

export default {
  components: {
    PositionsFiltersAndViewMode,
    WizardAlert,
    PositionsBySummary,
    SomePositionIncomplete,
  },
  data() {
    return {
      positionType: null,
      filters: {},
    }
  },
  computed: {
    ...mapState(['user']),
  },
  watch: {
    positionType: {
      async handler() {
        if (this.positionType) await this.updateFilters()
      },
    },
  },
  async mounted() {
    this.positionType = get(this.user, 'preferences.defaultPositionsNeCash', null)
      ? 'allNeCash'
      : 'all'
  },
  methods: {
    async updateFilters() {
      if (this.$refs.positionsFilters) await this.$refs.positionsFilters.updateFilters()
      this.init()
    },
    async init() {
      const filters = JSON.parse(JSON.stringify(this.filters))
      const type = this.positionType

      if (this.$refs.positionsByTicker) this.$refs.positionsByTicker.loadSummary({ filters, type })
      if (this.$refs.positionsByType) this.$refs.positionsByType.loadSummary({ filters, type })
      if (this.$refs.positionsByCurrency)
        this.$refs.positionsByCurrency.loadSummary({ filters, type })
      if (this.$refs.positionsByCountry)
        this.$refs.positionsByCountry.loadSummary({ filters, type })
      if (this.$refs.positionsByExposureCountry)
        this.$refs.positionsByExposureCountry.loadSummary({ filters, type })
      if (this.$refs.positionsByExchange)
        this.$refs.positionsByExchange.loadSummary({ filters, type })
      if (this.$refs.positionsBySector) this.$refs.positionsBySector.loadSummary({ filters, type })
      if (this.$refs.positionsByTags) this.$refs.positionsByTags.loadSummary({ filters, type })
      if (this.$refs.positionsByManager)
        this.$refs.positionsByManager.loadSummary({ filters, type })
    },
    async filterPositions(filters) {
      this.filters = JSON.parse(JSON.stringify(filters))
      this.init(filters)
    },
  },
}
</script>
